import { scrubPiiForLlm } from "../pii-scrubber.js";
import { READ_PEOPLE_MINE } from "../registry/pages/read-people-mine.js";
import { findEnumChoiceInSentence, resolveEnumSlot } from "../resolvers/enum-slot.js";
import { sameWord, wordsOf, type Word } from "../resolvers/vi-words.js";
import type { EnumFallbackPageLike, EnumSlotFill } from "./enum-fallback.js";
import { judgeSpan, type InterpreterSpanLike } from "./output.js";

/**
 * 🔴 US-AIV2-tourguide-0011 (@UC0371 · QĐ PO D3 · AC6) — LƯỚI TẤT ĐỊNH chỉ số `việc chờ tôi` của READ-PEOPLE-MINE cho câu hỏi "LÀM GÌ TIẾP".
 *
 * ## Vấn đề (đo, không đoán)
 * LLM thật 04/10 (thế giới L1, `thang` — thủ quỹ phụ có 1 lời báo chờ duyệt, n=3): *"toi can lam gi tiep theo"* (VÍ DỤ của Sổ cho chính trang này) ⇒
 * Phiếu chọn ĐÚNG READ-PEOPLE-MINE nhưng `spans: []` 3/3 ⇒ ô `chiSo` rơi mặc định Sổ (`khoản của tôi`, US-AIV2-0111 C10) ⇒ *"Ở nhóm …, bạn không
 * còn khoản nào phải đóng."* + một dòng thẻ — không phải lượt `ASKED_NEXT` của Người dẫn đường mà QĐ D3 hứa (TG-INV-10). Sổ ĐÃ khai đồng nghĩa
 * ("cần làm", "làm gì tiếp", "việc gì", "chờ tôi") — khi model gạch, resolver đọc đúng; cái hỏng là model KHÔNG gạch. Sửa prompt là đổi thứ model
 * thấy cho mọi ca (thu lại eval) mà vẫn là xác suất ⇒ chốt CODE, 0 LLM — cùng khuôn với `fillPeopleIndicatorFromSentence` (READ-PEOPLE-MINE-WRONG-ANSWER).
 *
 * ## Luật (MỘT hàm; Người làm gọi ở lượt mở việc từ câu — cùng chỗ với H02/S19/L07/lưới câu hỏi về người)
 * Điền ô `chiSo` của READ-PEOPLE-MINE khi và chỉ khi:
 *  0. trang là READ-PEOPLE-MINE (`READ_PEOPLE_MINE.id` — không literal);
 *  1. Phiếu ĐÃ NEO không có span nào cho `chiSo` — lưới KHÔNG BAO GIỜ đè chữ model đã gạch;
 *  2. MỌI chữ của câu (đã cạo PII) thuộc một khuôn ĐÓNG dưới đây — câu hỏi "tôi cần làm gì (tiếp)" / "có việc gì chờ tôi", không gì khác;
 *  3. CẢ câu nhắc ĐÚNG MỘT lựa chọn của ô (`findEnumChoiceInSentence` — nhãn/đồng nghĩa của Sổ) và đó là `việc chờ tôi`;
 *  4. đoạn chữ khớp qua phép neo (`judgeSpan`, phạm vi `null`) và resolver THẬT của ô đọc ra `việc chờ tôi`.
 * Chữ điền là CHỮ CỦA CÂU ("cần làm", "làm gì tiếp", "việc gì"…) — luật chung 1 (`deterministic-nets.ts`).
 *
 * Khuôn (so theo TỪ, `sameWord`: gõ không dấu khớp chữ có dấu):
 *   NGƯỜI = tôi | mình | em | chị | anh | cháu | bác | chúng tôi | chúng ta      LÚC = bây giờ | giờ | hôm nay
 *   SAU   = {tiếp [theo] | nữa | bây giờ | giờ | hôm nay}*                     ĐUÔI = {vậy|thế|nhỉ|ạ|à|hả|đây|đó|nhé|nha|ơi}*
 *   (A1) [xin hỏi] [LÚC] [NGƯỜI] [LÚC] [còn] cần làm gì SAU ĐUÔI           — "tôi cần làm gì tiếp theo", "giờ mình cần làm gì nữa ạ"
 *   (A2) [xin hỏi] [LÚC] [NGƯỜI] làm gì tiếp [theo] [bây giờ|giờ|nữa] ĐUÔI  — "làm gì tiếp theo", "lam gi tiep"
 *   (A3) [xin hỏi] [có] việc gì [đang] {chờ|đợi} NGƯỜI [không] ĐUÔI          — "có việc gì đang chờ tôi không"
 *   (A4) [xin hỏi] [NGƯỜI] {có|còn} việc gì {cần|phải} làm {không|nữa}* ĐUÔI — "tôi còn việc gì cần làm không"
 *   (A5) việc chờ NGƯỜI ĐUÔI                                                 — "việc chờ tôi" (nhãn của chính lựa chọn)
 *
 * ## Vì sao an toàn
 *  - Khuôn không có chỗ cho phủ định ("tôi không cần làm gì nữa"), cho mục đích/đối tượng ("cần làm gì ĐỂ đóng tiền quỹ"), cho tên riêng ("chị
 *    Lan cần làm gì") ⇒ ngoài khuôn ⇒ đường cũ (span model / mặc định Sổ). Câu hỏi nên KHÔNG đo `hasNegationOrDoubt` (luật chung 4 — lệch có địa
 *    chỉ, như M07/M09/lưới câu hỏi về người): "gì"/"không" là hình của chính câu hỏi; khuôn tự giới hạn phủ định.
 *  - Trang ĐỌC, 0 phiếu, 0 ghi: chỉ số `việc chờ tôi` = lượt Người dẫn đường (thẻ + nút đã kiểm `ready()`), CÙNG thứ lời chào nói.
 *  - Chiều sai tệ nhất: một câu hỏi "làm gì" nhận danh sách việc thay vì câu khoản — danh sách GỒM luôn khoản phải đóng (thẻ nợ).
 *  - Lưới chỉ chạy trong trang ĐÃ mở (LLM thật chọn) ⇒ không vòng qua kill-switch / LLM giả (luật chung 3).
 */
export const ASKED_NEXT_CHOICE = "việc chờ tôi";
const ASKED_NEXT_SLOT = "chiSo";

/**
 * Từ vựng ĐÓNG — chữ CÓ DẤU chuẩn. Cố ý KHÔNG có "cho" (cho tôi hỏi): dạng bỏ dấu "cho" trùng "chờ" ⇒ câu không dấu "co viec gi dang cho toi" phải
 * đọc được; "con" (đại từ) cũng không — trùng dạng bỏ dấu với "còn" ("toi con viec gi can lam").
 */
const LEXICON: readonly { readonly word: Word; readonly canon: string }[] = [
  "tôi", "mình", "em", "chị", "anh", "cháu", "bác", "chúng", "ta",
  "xin", "hỏi", "bây", "giờ", "hôm", "nay",
  "cần", "phải", "làm", "gì", "tiếp", "theo", "nữa",
  "có", "còn", "việc", "đang", "chờ", "đợi", "không",
  "vậy", "thế", "nhỉ", "ạ", "à", "hả", "đây", "đó", "nhé", "nha", "ơi",
].map((w) => ({ word: wordsOf(w)[0]!, canon: w === "à" ? "ạ" : w }));

const WHO = "(?:tôi|mình|em|chị|anh|cháu|bác|chúng tôi|chúng ta)";
const ASK = "(?:xin hỏi )?";
const NOW = "(?:bây giờ|giờ|hôm nay)";
const AFTER = "(?: (?:tiếp(?: theo)?|nữa|bây giờ|giờ|hôm nay))*";
const TAIL = "(?: (?:vậy|thế|nhỉ|ạ|hả|đây|đó|nhé|nha|ơi))*";

const PATTERNS: readonly RegExp[] = [
  `${ASK}(?:${NOW} )?(?:${WHO} )?(?:${NOW} )?(?:còn )?cần làm gì${AFTER}${TAIL}`,
  `${ASK}(?:${NOW} )?(?:${WHO} )?làm gì tiếp(?: theo)?(?: (?:bây giờ|giờ|nữa))?${TAIL}`,
  `${ASK}(?:có )?việc gì (?:đang )?(?:chờ|đợi) ${WHO}(?: không)?${TAIL}`,
  `${ASK}(?:${WHO} )?(?:có|còn) việc gì (?:cần|phải) làm(?: (?:không|nữa))*${TAIL}`,
  `việc chờ ${WHO}${TAIL}`,
].map((p) => new RegExp(`^${p}$`, "u"));

/** Câu (đã cạo PII) thuộc một trong năm khuôn câu hỏi "làm gì tiếp". Một từ ngoài từ vựng ⇒ `false`. */
function isAskedNextQuestion(text: string): boolean {
  const canon: string[] = [];
  for (const w of wordsOf(text)) {
    const hit = new Set(LEXICON.filter((l) => sameWord(w, l.word)).map((l) => l.canon));
    if (hit.size !== 1) return false;
    canon.push([...hit][0]!);
  }
  const line = canon.join(" ");
  return line !== "" && PATTERNS.some((re) => re.test(line));
}

/**
 * @param sentence câu người dùng (thô hoặc đã cạo — hàm tự cạo, idempotent).
 * @param anchoredSpans span của Phiếu **ĐÃ NEO** cho CHÍNH trang này (`envSpans` của Người làm).
 * @param page trang đang mở (Sổ); `null` ⇒ không áp.
 */
export function fillAskedNextIndicatorFromSentence(
  sentence: string,
  anchoredSpans: readonly InterpreterSpanLike[],
  page: (EnumFallbackPageLike & { readonly id: string }) | null,
): EnumSlotFill[] {
  if (page === null || page.id !== READ_PEOPLE_MINE.id) return [];
  const slot = page.slots.find((s) => s.id === ASKED_NEXT_SLOT);
  if (slot === undefined || slot.type !== "enum") return [];
  if (anchoredSpans.some((s) => s.slot === slot.id)) return [];
  const text = scrubPiiForLlm(sentence).text.normalize("NFC").trim();
  if (!isAskedNextQuestion(text)) return [];
  const match = findEnumChoiceInSentence(text, slot);
  if (match === null || match.choice !== ASKED_NEXT_CHOICE) return [];
  const verdict = judgeSpan(text, { slot: slot.id, text: text.slice(match.start, match.end), start: match.start, end: match.end }, null);
  if (!verdict.kept) return [];
  const r = resolveEnumSlot(verdict.span.text, slot);
  if (r.status !== "resolved" || r.value !== ASKED_NEXT_CHOICE) return [];
  return [{ slot: slot.id, text: verdict.span.text }];
}
